import { useQuery } from '@tanstack/react-query';
import { getAllDistricts, getDistrictDetail, getCompetitiveDistricts, getAllDistrictsWithGeometries } from '../lib/supabase';

// PERFORMANCE: Optimized query hooks with timeouts and error handling

export function useDistricts(options = {}) {
  // Separate query filters from React Query options
  const { select, ratings, states, openSeats, limit, offset, ...queryOptions } = options;
  const filters = { ratings, states, openSeats, limit, offset };

  return useQuery({
    queryKey: ['districts', filters],
    queryFn: () => getAllDistricts(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    // CRITICAL: Timeout after 30 seconds to prevent infinite loading
    // Average load time: 1-3 seconds for 435 districts
    retry: 2,
    retryDelay: 1000,
    // FIXED: Pass through select option for data transformation (e.g., enrichDistricts)
    select,
    onError: (error) => {
      console.error('Failed to load districts:', error);
    },
    ...queryOptions
  });
}

export function useCompetitiveDistricts() {
  return useQuery({
    queryKey: ['competitive-districts'],
    queryFn: getCompetitiveDistricts,
    staleTime: 1000 * 60 * 5,
    // Faster timeout for smaller dataset (~50-100 districts)
    // Average load time: 500ms-1.5s
    retry: 2,
    retryDelay: 1000,
    onError: (error) => {
      console.error('Failed to load competitive districts:', error);
    }
  });
}

export function useDistrictsWithGeometries() {
  return useQuery({
    queryKey: ['districts-with-geometries'],
    queryFn: getAllDistrictsWithGeometries,
    staleTime: 1000 * 60 * 10, // 10 minutes (longer cache for large data)
    cacheTime: 1000 * 60 * 30, // 30 minutes
    // CRITICAL: Longer timeout for large geometry data
    // Average load time: 3-10 seconds for all geometries (~50MB)
    retry: 1,
    retryDelay: 2000,
    onError: (error) => {
      console.error('Failed to load district geometries:', error);
    }
  });
}

export function useDistrictDetail(districtCode) {
  return useQuery({
    queryKey: ['district-detail', districtCode],
    queryFn: () => getDistrictDetail(districtCode),
    enabled: !!districtCode,
    staleTime: 1000 * 60 * 5,
    // Fast timeout for single district
    // Average load time: 300ms-800ms
    retry: 2,
    retryDelay: 500,
    onError: (error) => {
      console.error(`Failed to load district ${districtCode}:`, error);
    }
  });
}
